/**
 * Análisis de ejecución:
 * Extrae una serie temporal de ángulos clave del video, y la compara
 * contra otra serie (modelo de referencia vs gimnasta).
 *
 * Los ángulos clave son agnósticos al ejercicio: cubren los principales
 * vectores biomecánicos.
 */

import { LM, angle, midpoint, linealidad, bodyHeight } from './landmarks.js';

const KEY_METRICS = [
  {
    id: 'shoulder_L',
    name: 'Bloqueo hombro izq.',
    compute: lm => angle(lm[LM.L_WRIST], lm[LM.L_SHOULDER], lm[LM.L_HIP]),
  },
  {
    id: 'shoulder_R',
    name: 'Bloqueo hombro der.',
    compute: lm => angle(lm[LM.R_WRIST], lm[LM.R_SHOULDER], lm[LM.R_HIP]),
  },
  {
    id: 'knee_L',
    name: 'Rodilla izq.',
    compute: lm => angle(lm[LM.L_HIP], lm[LM.L_KNEE], lm[LM.L_ANKLE]),
  },
  {
    id: 'knee_R',
    name: 'Rodilla der.',
    compute: lm => angle(lm[LM.R_HIP], lm[LM.R_KNEE], lm[LM.R_ANKLE]),
  },
  {
    id: 'elbow_L',
    name: 'Codo izq.',
    compute: lm => angle(lm[LM.L_SHOULDER], lm[LM.L_ELBOW], lm[LM.L_WRIST]),
  },
  {
    id: 'elbow_R',
    name: 'Codo der.',
    compute: lm => angle(lm[LM.R_SHOULDER], lm[LM.R_ELBOW], lm[LM.R_WRIST]),
  },
  {
    id: 'hip_opening',
    name: 'Apertura de piernas',
    compute: lm => angle(lm[LM.L_ANKLE], midpoint(lm[LM.L_HIP], lm[LM.R_HIP]), lm[LM.R_ANKLE]),
  },
  {
    id: 'body_line',
    name: 'Alineación del cuerpo',
    compute: lm => {
      const sh = midpoint(lm[LM.L_SHOULDER], lm[LM.R_SHOULDER]);
      const hp = midpoint(lm[LM.L_HIP], lm[LM.R_HIP]);
      const an = midpoint(lm[LM.L_ANKLE], lm[LM.R_ANKLE]);
      if (!sh || !hp || !an) return NaN;
      return linealidad([sh, hp, an], bodyHeight(lm)) * 1000; // escalado para gráfica
    },
  },
];

export function getKeyMetrics() {
  return KEY_METRICS.map(m => ({ id: m.id, name: m.name }));
}

/** Calcula todas las métricas para un frame. */
export function computeMetrics(lm) {
  const out = {};
  for (const m of KEY_METRICS) {
    const v = m.compute(lm);
    out[m.id] = Number.isFinite(v) ? v : null;
  }
  return out;
}

/**
 * Procesa un video local (blob URL) y extrae landmarks + métricas por frame
 * muestreando a `sampleFps` por segundo.
 *
 * @param {string} videoUrl - Object URL del video.
 * @param {object} poseLandmarker - Instancia MediaPipe existente.
 * @param {function} smootherFactory - () => smoother(landmarks, tMs) (One Euro).
 * @param {function} onProgress - (0..1)
 * @param {number} sampleFps
 * @returns {Promise<{duration:number, timeline:Array, metrics:Array}>}
 */
export async function processVideo(videoUrl, poseLandmarker, smootherFactory, onProgress, sampleFps = 12) {
  const video = document.createElement('video');
  video.src = videoUrl;
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';

  await new Promise((res, rej) => {
    video.onloadedmetadata = () => res();
    video.onerror = () => rej(new Error('No se pudo cargar el video de referencia.'));
  });

  const duration = video.duration || 0;
  const step = 1 / sampleFps;
  const smoother = smootherFactory();
  const timeline = [];
  const metrics = [];

  // MediaPipe exige timestamps monotónicamente crecientes.
  let ts = performance.now() + 1;

  const total = Math.max(1, Math.floor(duration / step));
  let processed = 0;

  for (let t = 0; t < duration; t += step) {
    // posicionar el video en t segundos
    await new Promise((res) => {
      const onSeeked = () => { video.removeEventListener('seeked', onSeeked); res(); };
      video.addEventListener('seeked', onSeeked);
      video.currentTime = Math.min(t, duration - 0.01);
    });

    try {
      ts += 33; // delta arbitrario pero creciente
      const result = poseLandmarker.detectForVideo(video, ts);
      if (result && result.landmarks && result.landmarks.length > 0) {
        const smoothed = smoother(result.landmarks[0], ts);
        const tMs = t * 1000;
        timeline.push({ t: tMs, lm: smoothed });
        metrics.push({ t: tMs, ...computeMetrics(smoothed) });
      }
    } catch (e) {
      // frame skippable
    }

    processed += 1;
    if (onProgress) onProgress(Math.min(1, processed / total));
  }

  return { duration: duration * 1000, timeline, metrics };
}

/**
 * Resamplea una serie temporal [{t, ...metrics}] a N puntos uniformes
 * en el rango [0, duration]. Útil para comparar videos de distinta longitud.
 */
export function resample(series, duration, n = 30) {
  if (!series.length) return [];
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = (i / (n - 1)) * duration;
    // encontrar el par de puntos que rodean t
    let lo = 0, hi = series.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (series[mid].t <= t) lo = mid; else hi = mid;
    }
    const a = series[lo], b = series[hi];
    const alpha = b.t > a.t ? (t - a.t) / (b.t - a.t) : 0;
    const point = { t };
    for (const k of Object.keys(a)) {
      if (k === 't') continue;
      const va = a[k], vb = b[k];
      if (va == null || vb == null) { point[k] = va ?? vb; continue; }
      point[k] = va + (vb - va) * alpha;
    }
    out.push(point);
  }
  return out;
}

/**
 * Compara dos timelines de métricas (modelo y gimnasta) normalizándolas
 * a 30 puntos uniformes en tiempo y calculando RMSE por métrica.
 */
export function compareSeries(refMetrics, refDuration, gymMetrics, gymDuration) {
  const N = 30;
  const ref = resample(refMetrics, refDuration, N);
  const gym = resample(gymMetrics, gymDuration, N);
  const result = {};

  for (const m of KEY_METRICS) {
    let sumSq = 0, count = 0;
    let maxDiff = 0, maxAt = 0;
    for (let i = 0; i < N; i++) {
      const r = ref[i]?.[m.id], g = gym[i]?.[m.id];
      if (r == null || g == null) continue;
      const d = r - g;
      sumSq += d * d; count += 1;
      if (Math.abs(d) > Math.abs(maxDiff)) { maxDiff = d; maxAt = i / (N - 1); }
    }
    const rmse = count ? Math.sqrt(sumSq / count) : null;
    result[m.id] = {
      name: m.name,
      rmse,
      maxDiff,
      maxAt, // 0..1 (porcentaje de la ejecución)
      refSeries: ref.map(p => p[m.id]),
      gymSeries: gym.map(p => p[m.id]),
    };
  }

  // Score global: 100 menos penalización por RMSE promedio (solo para ángulos, no body_line)
  const angularMetrics = Object.entries(result).filter(([k]) => k !== 'body_line');
  const rmseList = angularMetrics.map(([, v]) => v.rmse).filter(x => x != null);
  const avgRmse = rmseList.length ? rmseList.reduce((a, b) => a + b, 0) / rmseList.length : 0;
  const similarity = Math.max(0, Math.min(100, 100 - avgRmse * 2));

  return { similarity, perMetric: result, avgRmse };
}
